import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useForm } from "@mantine/form";
import Box from "../../../components/Box";
import Button from "../../../components/Button";
import Errors from "../../../components/Errors";
import Input from "../../../components/Input";
import usePassword from "../../../components/Input/hooks/usePassword";
import TableFlow from "../../../components/TableFlow";
import Footnote from "../components/Footnote";
import oauthSignInUpHandler from "../../../api/oauthSignInUpHandler";
import usePasswordReset from "../../../api/usePasswordReset";
import { capitalize } from "../../../utils/capitalize";
import validatePassword from "../../../utils/validatePassword";
import { PasswordResetProps } from "../types";
import style from "../style/Form.module.scss";
import { thirdPartyProviders } from "../Login";

export default function PasswordResetChangePassword({ token }: PasswordResetProps) {
  const [searchParams] = useSearchParams();
  const isInvite = searchParams.get("invite") === "true";
  const [isLoadingSSO, setIsLoadingSSO] = useState(false);
  const [ssoError, setSsoError] = useState("");

  const form = useForm({
    initialValues: {
      token: token || "",
      password: "",
    },
    validate: {
      password: (value) => (value && !validatePassword(value)[0] ? validatePassword(value)[1] : null),
    },
  });

  const { mutate, isLoading, error, isSuccess } = usePasswordReset();

  useEffect(() => {
    if (isSuccess) window.location.href = "/";
  }, [isSuccess]);

  const onSubmit = (values: any) => {
    mutate(values);
  };

  const passwordProps = usePassword();

  if (isInvite) {
    return (
      <>
        <Box className={style.boxContainer}>
          <div className={style.container}>
            <div className={style.logo}>
              <TableFlow color size="big" />
            </div>

            <p className={style.welcome}>Join your team on TableFlow.</p>

            <div className={style.oauthButtons}>
              {thirdPartyProviders.map((provider, i) => {
                return (
                  <Button
                    key={i}
                    icon={provider.icon}
                    variants={["noFill", "fullWidth"]}
                    disabled={isLoading || isLoadingSSO}
                    onClick={() => {
                      setIsLoadingSSO(true);
                      oauthSignInUpHandler(provider.key).then((err) => {
                        if (err) {
                          setSsoError(err);
                          setIsLoadingSSO(false);
                        }
                      });
                    }}>
                    Sign up with {provider.name}
                  </Button>
                );
              })}
            </div>

            <div className={style.separator}>Or</div>

            <form onSubmit={form.onSubmit(onSubmit)}>
              <Input label="Password" name="password" {...form.getInputProps("password")} {...passwordProps} required autoComplete="on" />

              <div className={style.actions}>
                <Button type="submit" variants={["primary", "fullWidth"]} className={style.button} disabled={isLoading || isLoadingSSO}>
                  {isLoading ? "Please Wait..." : "Create Account"}
                </Button>
              </div>

              <Errors error={error || ssoError} />
            </form>
          </div>
        </Box>
        <Footnote />
      </>
    );
  }

  return (
    <>
      <Box className={style.boxContainer}>
        <h2 className={style.title}>Reset Password</h2>

        <form onSubmit={form.onSubmit(onSubmit)}>
          <Input label="New password" name="password" {...form.getInputProps("password")} {...passwordProps} required autoComplete="on" />

          <div className={style.actions}>
            <Button type="submit" variants={["primary", "fullWidth"]} className={style.button} disabled={isLoading}>
              {isLoading ? "Please Wait..." : "Reset"}
            </Button>
          </div>

          <Errors error={error} />
        </form>
      </Box>
    </>
  );
}
